import {Component, EventEmitter, Input, Output} from '@angular/core';
import {
  QualifyingRuleSourceMaximumPriceSortDirection,
  QualifyingRuleSourceRow
} from '../models/qualifying-rule-source.model';
import {
  QualifyingRuleSourceDiscoveryState,
  QualifyingRuleSourceDiscoveryStatus
} from '../services/qualifying-rule-source.service';

interface QualifyingVehiclesPageEvent {
  readonly page?: number;
}

const firstPageIndex = 0;
const firstPageNumber = 1;
const missingValueLabel = 'Not provided';
const unavailableReason = 'This source is not selectable.';
const ascendingSortDirection: QualifyingRuleSourceMaximumPriceSortDirection = 'asc';
const descendingSortDirection: QualifyingRuleSourceMaximumPriceSortDirection = 'desc';

@Component({
  selector: 'auto-ipop-rule-workflow-qualifying-vehicles',
  templateUrl: './ipop-rule-workflow-qualifying-vehicles.component.html',
  styleUrls: ['./ipop-rule-workflow-qualifying-vehicles.component.scss'],
  standalone: false
})
export class IpopRuleWorkflowQualifyingVehiclesComponent
{
  @Input() discoveryState: QualifyingRuleSourceDiscoveryState | null = null;
  @Input() selectedSource: QualifyingRuleSourceRow | null = null;
  @Input() selectionEnabled = true;
  @Input() criteriaPreview = false;
  @Input() pageSize = 5;
  @Input() maximumPriceSortDirection: QualifyingRuleSourceMaximumPriceSortDirection = ascendingSortDirection;

  @Output() sourceSelected = new EventEmitter<QualifyingRuleSourceRow>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() maximumPriceSortDirectionChanged = new EventEmitter<QualifyingRuleSourceMaximumPriceSortDirection>();
  @Output() retryRequested = new EventEmitter<void>();

  readonly discoveryStatus = QualifyingRuleSourceDiscoveryStatus;

  /** Emits a selectable source while preserving offer-set identity. */
  onSourceChange(source: QualifyingRuleSourceRow): void
  {
    if (this.selectionEnabled
      && source.selectable
      && Number.isSafeInteger(source.offerSetId)
      && source.offerSetId > 0
      && !this.isSelected(source)) {
      this.selectedSource = {...source};
      this.sourceSelected.emit({...source});
    }
  }

  /** Converts the PrimeNG zero-based page event to the server page number. */
  onPageChange(event: QualifyingVehiclesPageEvent): void
  {
    let pageIndex = firstPageIndex;
    if (event.page !== undefined && Number.isSafeInteger(event.page) && event.page >= firstPageIndex) {
      pageIndex = event.page;
    }
    const page = pageIndex + firstPageNumber;

    this.pageChanged.emit(page);
  }

  /** Requests the current validated discovery criteria again. */
  onRetry(): void
  {
    this.retryRequested.emit();
  }

  /** Toggles the one supported server-backed sort direction. */
  onMaximumPriceSort(): void
  {
    let direction = ascendingSortDirection;
    if (this.maximumPriceSortDirection === ascendingSortDirection) {
      direction = descendingSortDirection;
    }
    this.maximumPriceSortDirectionChanged.emit(direction);
  }

  /** Exposes the current Maximum Price direction to assistive technology. */
  getMaximumPriceAriaSort(): 'ascending' | 'descending'
  {
    let ariaSort: 'ascending' | 'descending' = 'ascending';
    if (this.maximumPriceSortDirection === descendingSortDirection) {
      ariaSort = 'descending';
    }

    return ariaSort;
  }

  /** Returns the icon for the current Maximum Price direction. */
  getMaximumPriceSortIcon(): string
  {
    let icon = 'pi-sort-amount-up';
    if (this.maximumPriceSortDirection === descendingSortDirection) {
      icon = 'pi-sort-amount-down';
    }

    return icon;
  }

  /** Tracks each server row by its authoritative source identity. */
  trackByOfferSetId(_index: number, source: QualifyingRuleSourceRow): number
  {
    const offerSetId = source.offerSetId;

    return offerSetId;
  }

  /** Reports whether the supplied row is the one selected source. */
  isSelected(source: QualifyingRuleSourceRow): boolean
  {
    let selected = false;
    if (this.selectedSource !== null) {
      selected = this.selectedSource.offerSetId === source.offerSetId;
    }

    return selected;
  }

  /** Builds a stable radio-control identifier from the offer-set ID. */
  getRadioId(source: QualifyingRuleSourceRow): string
  {
    const radioId = `qualifying-source-${source.offerSetId}`;

    return radioId;
  }

  /** Builds an identifier for a disabled source explanation. */
  getDisabledReasonId(source: QualifyingRuleSourceRow): string
  {
    const reasonId = `qualifying-source-${source.offerSetId}-reason`;

    return reasonId;
  }

  /** Associates disabled radios only with their corresponding explanation. */
  getDisabledDescriptionId(source: QualifyingRuleSourceRow): string | null
  {
    let descriptionId: string | null = null;
    if (!source.selectable) {
      descriptionId = this.getDisabledReasonId(source);
    }

    return descriptionId;
  }

  /** Creates an accessible label that disambiguates similar source rows. */
  getSourceLabel(source: QualifyingRuleSourceRow): string
  {
    const vehicle = this.getVehicleIdentity(source);
    const label = `Select ${source.title}, ${vehicle}, offer-set ID ${source.offerSetId}`;

    return label;
  }

  /** Creates a concise vehicle identity from authoritative row fields. */
  getVehicleIdentity(source: QualifyingRuleSourceRow): string
  {
    const values = [source.year.toString(), source.make, source.model];
    if (source.trim !== null && source.trim.trim() !== '') {
      values.push(source.trim);
    }
    const identity = values.join(' ');

    return identity;
  }

  /** Returns a meaningful explanation for every disabled row. */
  getDisabledReason(source: QualifyingRuleSourceRow): string
  {
    let reason = unavailableReason;
    if (source.disabledReason !== null && source.disabledReason.trim() !== '') {
      reason = source.disabledReason;
    }

    return reason;
  }

  /** Normalizes optional display text without changing typed source state. */
  getOptionalText(value: string | null): string
  {
    let displayValue = missingValueLabel;
    if (value !== null && value.trim() !== '') {
      displayValue = value;
    }

    return displayValue;
  }

  /** Reports whether the API supplied at least one offer summary value. */
  hasOfferSummary(source: QualifyingRuleSourceRow): boolean
  {
    const hasSummary = source.offerSummary.trim() !== '';

    return hasSummary;
  }

  /** Announces current loading and result-count state politely. */
  getLiveAnnouncement(): string
  {
    let announcement = '';
    const state = this.discoveryState;
    if (state !== null && state.status === QualifyingRuleSourceDiscoveryStatus.Loading) {
      announcement = 'Loading qualifying vehicles.';
    }
    if (state !== null && state.page !== null && state.status !== QualifyingRuleSourceDiscoveryStatus.Loading) {
      announcement = `${state.page.pagination.totalItems} qualifying vehicles found.`;
    }

    return announcement;
  }
}
